import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { DataReport } from './data-report';
import { SharedService } from '../_shared/shared.service';

/**
 * Reads/writes the Quality Test framework under `/api/2.6/quality-tests/*`.
 *
 * A4 of the DataReport → QualityTest migration (DATA_REPORT_TO_QUALITY_TEST_MIGRATION.md)
 * flipped `urlEndpoint` from `dataReports` to `quality-tests`. All consuming
 * components were updated to read `res.data.qualityTests` (with a
 * `|| res.data.dataReports` fallback for safety during the deprecation window).
 *
 * The class name is retained as `DataReportService` for now to avoid a
 * sweeping rename across 8 consumers; it will be renamed to
 * `QualityTestReportService` in PR2 when the legacy controller is deleted.
 */
@Injectable({ providedIn: 'root' })
export class DataReportService {
	private readonly rptQuartzHost = 'https://rpt.kbmhawaii.com';
	private readonly rptQuartzApiBase = `${this.rptQuartzHost}/api/2.6/quartz-jobs`;

	statuses: any = [
		{ value: '0', name: 'Active' },
		{ value: '2', name: 'Suspended' },
		{ value: '1', name: 'Canceled' }
	];
	
	statusesForPropertyStatus: any = [
		{ value: 0, name: 'Active' },
		{ value: 2, name: 'Suspended' },
		{ value: 1, name: 'Canceled' }
	];
	
	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService,
		) { }

	// A4 migration: flipped from 'dataReports' to 'quality-tests'. Every method
	// that builds a URL from `${this.urlEndpoint}` now targets the QualityTest
	// controller, which exposes the same endpoint surface (CRUD, listing/rates/
	// fee-and-tax/property-profile-quality, fetch-vrbo/google/quartz-job-data,
	// fetch-google-sync-dashboard, testResultStatus, testName, /{id}/audit).
	urlEndpoint = 'quality-tests';

	add(dataReport: DataReport): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(dataReport));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, dataReport);
	}

	update(dataReport: DataReport): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, dataReport);
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe();
	}

	// getReports(): Observable<ApiResponse> {
	// 	return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe();
	// }

	getReports(params?: any): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params }).pipe();
	}
	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	qualityTestReport(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/quality-tests-report`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	runQuality(endpoint: string, rentalIds: number[]) {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${endpoint}`, rentalIds).pipe();
	}


	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}
	
	testDataReport(shortTermRentalIdList: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/listing-quality`,shortTermRentalIdList);
	}

	testFeeAndTaxQualityStatus(shortTermRentalIdList: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/fee-and-tax-quality`,shortTermRentalIdList);
	}

	testRateQualityStatus(shortTermRentalIdList: any): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/rates-quality`,shortTermRentalIdList);
	}
	
	testAllDataReport(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/listing-quality-all`).pipe(retry({ count: 2, delay: 1500 }));
	}

	// Fetch VRBO data reports by testName (e.g., 'VrboPropertySync')
	fetchVrboData(testName: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/fetch-vrbo-data/${encodeURIComponent(testName)}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	// Fetch Google data reports by testName (e.g., 'GoogleUnitAvailabilitySync')
	// Backend now accepts daysBack/page/size filters so the page only loads
	// recent activity instead of the entire str_data_report history.
	fetchGoogleData(testName: string, daysBack: number = 7, page: number = 0, size: number = 500): Observable<ApiResponse> {
		const params = new HttpParams()
			.set('daysBack', String(daysBack))
			.set('page', String(page))
			.set('size', String(size));
		return this.http.get<ApiResponse>(
			`${environment.apiUrl}/${this.urlEndpoint}/fetch-google-data/${encodeURIComponent(testName)}`,
			{ params }
		).pipe(retry({ count: 2, delay: 1500 }));
	}

	// Aggregated counts (PASS / WARN / FAIL per feed) for the last N days.
	// Backed by /dataReports/fetch-google-sync-dashboard which runs a single
	// GROUP BY query — no raw rows shipped to the browser.
	fetchGoogleSyncDashboard(daysBack: number = 3): Observable<ApiResponse> {
		const params = new HttpParams().set('daysBack', String(daysBack));
		return this.http.get<ApiResponse>(
			`${environment.apiUrl}/${this.urlEndpoint}/fetch-google-sync-dashboard`,
			{ params }
		).pipe(retry({ count: 2, delay: 1500 }));
	}

	// Fetch all Quartz job data reports
	fetchQuartzJobDataAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/fetch-quartz-job-data`).pipe(retry({ count: 2, delay: 1500 }));
	}

	// Fetch Quartz job data reports by job name (testName)
	fetchQuartzJobDataByJobName(testName: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/fetch-quartz-job-data/${encodeURIComponent(testName)}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	triggerQuartzJobNowRpt(quartzJobName: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${this.rptQuartzApiBase}/trigger-now/${encodeURIComponent(quartzJobName)}`).pipe(retry(1));
	}

	resetQuartzTriggerStateRpt(quartzJobName: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${this.rptQuartzApiBase}/reset-trigger-state/${encodeURIComponent(quartzJobName)}`).pipe(retry(1));
	}

	recoverQuartzStaleStartedRpt(quartzJobName: string): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${this.rptQuartzApiBase}/recover-stale-started/${encodeURIComponent(quartzJobName)}`).pipe(retry(1));
	}

	// Fetch all Google data reports
	fetchGoogleDataAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/fetch-google-data`).pipe(retry({ count: 2, delay: 1500 }));
	}
	
	findTestResultStatusValues(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/testResultStatus`).pipe(retry({ count: 2, delay: 1500 }));
	}
	
	findTestNameValues(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/testName`).pipe(retry({ count: 2, delay: 1500 }));
	}


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
